import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { ScrollArea } from '../ui/scroll-area';
import { Plus, Trash2, ClipboardList, Pill, Activity, Calendar, Users, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'emergencia', label: 'Emergencia', color: 'bg-red-100 text-red-800' },
  { value: 'hospitalizacion', label: 'Hospitalización', color: 'bg-blue-100 text-blue-800' },
  { value: 'ambulatorio', label: 'Ambulatorio', color: 'bg-green-100 text-green-800' },
  { value: 'uci', label: 'UCI', color: 'bg-purple-100 text-purple-800' },
  { value: 'paliativo', label: 'Paliativo', color: 'bg-gray-100 text-gray-800' },
];

const VIA_OPTIONS = ['IV', 'VO', 'IM', 'SC', 'Nebulización', 'Naricera', 'Tópico', 'SL'];
const INDICACION_TIPOS = ['dieta', 'actividad', 'cuidados', 'monitoreo'];
const CONTROL_TIPOS = ['laboratorio', 'imagen', 'consulta', 'signos_vitales'];
const URGENCIA_OPTIONS = ['urgente', 'normal', 'electiva'];

export default function TreatmentPlanModal({ 
  isOpen, 
  onClose, 
  sessionId,
  emittedDiagnoses = [],
  onPlanCreated,
  existingPlan = null
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Form data
  const [categoria, setCategoria] = useState('emergencia');
  const [diagnosticoPrincipalId, setDiagnosticoPrincipalId] = useState('');
  const [medicamentos, setMedicamentos] = useState([]);
  const [indicaciones, setIndicaciones] = useState([]);
  const [controles, setControles] = useState([]);
  const [derivaciones, setDerivaciones] = useState([]);
  const [notas, setNotas] = useState('');

  // Temp inputs
  const [newMed, setNewMed] = useState({ nombre: '', dosis: '', frecuencia: '', via: 'IV', indicacion: '' });
  const [newInd, setNewInd] = useState({ tipo: 'cuidados', descripcion: '', prioridad: 'normal' });
  const [newCtrl, setNewCtrl] = useState({ tipo: 'laboratorio', descripcion: '', frecuencia: '' });
  const [newDer, setNewDer] = useState({ especialidad: '', motivo: '', urgencia: 'normal' });

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/treatment-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      // Reset form
      setSelectedTemplate(null);
      setCategoria('emergencia');
      setMedicamentos([]);
      setIndicaciones([]);
      setControles([]);
      setDerivaciones([]);
      setNotas('');
      
      // Set principal diagnosis if available
      const principal = emittedDiagnoses.find(d => d.tipo === 'principal');
      if (principal) {
        setDiagnosticoPrincipalId(principal.diagnosis_id);
      }
      
      // Load existing plan if editing
      if (existingPlan) {
        setCategoria(existingPlan.categoria || 'emergencia');
        setMedicamentos(existingPlan.medicamentos || []);
        setIndicaciones(existingPlan.indicaciones || []);
        setControles(existingPlan.controles || []);
        setDerivaciones(existingPlan.derivaciones || []);
        setNotas(existingPlan.notas || '');
      }
    }
  }, [isOpen, fetchTemplates, emittedDiagnoses, existingPlan]);

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setCategoria(template.categoria || 'emergencia');
    setMedicamentos(template.medicamentos || []);
    setIndicaciones(template.indicaciones || []);
    setControles(template.controles || []);
    setDerivaciones(template.derivaciones || []);
    setNotas(template.notas || '');
    toast.success(`Plantilla "${template.nombre}" aplicada`);
  };

  // Add handlers
  const addMedicamento = () => {
    if (newMed.nombre && newMed.dosis) {
      setMedicamentos([...medicamentos, { ...newMed }]);
      setNewMed({ nombre: '', dosis: '', frecuencia: '', via: 'IV', indicacion: '' });
    }
  };

  const addIndicacion = () => {
    if (newInd.descripcion) {
      setIndicaciones([...indicaciones, { ...newInd }]);
      setNewInd({ tipo: 'cuidados', descripcion: '', prioridad: 'normal' });
    }
  };

  const addControl = () => {
    if (newCtrl.descripcion) {
      setControles([...controles, { ...newCtrl }]);
      setNewCtrl({ tipo: 'laboratorio', descripcion: '', frecuencia: '' });
    }
  };

  const addDerivacion = () => {
    if (newDer.especialidad && newDer.motivo) {
      setDerivaciones([...derivaciones, { ...newDer }]);
      setNewDer({ especialidad: '', motivo: '', urgencia: 'normal' });
    }
  };

  // Remove handlers
  const removeItem = (list, setList, index) => {
    setList(list.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (medicamentos.length === 0 && indicaciones.length === 0) {
      toast.error('Agrega al menos un medicamento o indicación');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/simulations/${sessionId}/create-treatment-plan`,
        {
          categoria,
          diagnostico_principal_id: diagnosticoPrincipalId || null,
          medicamentos,
          indicaciones,
          controles,
          derivaciones,
          notas: notas || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Plan de tratamiento creado');
      
      if (onPlanCreated) {
        onPlanCreated(response.data);
      }
      
      onClose();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al crear plan';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-green-600" />
            {existingPlan ? 'Editar Plan de Tratamiento' : 'Crear Plan de Tratamiento'}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh] pr-4">
          <div className="space-y-4">
            {/* Templates */}
            {!existingPlan && templates.length > 0 && (
              <div>
                <Label className="mb-2 block">Aplicar plantilla (opcional)</Label>
                <div className="flex gap-2 flex-wrap">
                  {templates.slice(0, 5).map(t => (
                    <Button
                      key={t.id}
                      variant={selectedTemplate?.id === t.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => applyTemplate(t)}
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      {t.nombre}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría del Plan</Label>
                <Select value={categoria} onValueChange={setCategoria}>
                  <SelectTrigger data-testid="plan-categoria">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Diagnóstico Principal</Label>
                <Select value={diagnosticoPrincipalId} onValueChange={setDiagnosticoPrincipalId}>
                  <SelectTrigger data-testid="plan-diagnostico">
                    <SelectValue placeholder="Seleccionar diagnóstico" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin diagnóstico</SelectItem>
                    {emittedDiagnoses.map(d => (
                      <SelectItem key={d.diagnosis_id} value={d.diagnosis_id}>
                        {d.codigo_cie10} - {d.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Accordion type="multiple" defaultValue={["medicamentos"]} className="w-full">
              {/* Medicamentos */}
              <AccordionItem value="medicamentos">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Pill className="h-4 w-4 text-blue-600" />
                    Medicamentos ({medicamentos.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  <div className="grid grid-cols-6 gap-2">
                    <Input placeholder="Nombre" value={newMed.nombre} onChange={(e) => setNewMed({ ...newMed, nombre: e.target.value })} />
                    <Input placeholder="Dosis" value={newMed.dosis} onChange={(e) => setNewMed({ ...newMed, dosis: e.target.value })} />
                    <Input placeholder="Frecuencia" value={newMed.frecuencia} onChange={(e) => setNewMed({ ...newMed, frecuencia: e.target.value })} />
                    <Select value={newMed.via} onValueChange={(v) => setNewMed({ ...newMed, via: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VIA_OPTIONS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Indicación" value={newMed.indicacion} onChange={(e) => setNewMed({ ...newMed, indicacion: e.target.value })} />
                    <Button type="button" onClick={addMedicamento} size="icon"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-2">
                    {medicamentos.map((med, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-blue-50 p-2 rounded text-sm">
                        <span><strong>{med.nombre}</strong> {med.dosis} - {med.frecuencia} ({med.via})</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(medicamentos, setMedicamentos, idx)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Indicaciones */}
              <AccordionItem value="indicaciones">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-green-600" />
                    Indicaciones ({indicaciones.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  <div className="grid grid-cols-4 gap-2">
                    <Select value={newInd.tipo} onValueChange={(v) => setNewInd({ ...newInd, tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {INDICACION_TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="col-span-2" placeholder="Descripción" value={newInd.descripcion} onChange={(e) => setNewInd({ ...newInd, descripcion: e.target.value })} />
                    <Button type="button" onClick={addIndicacion} size="icon"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-2">
                    {indicaciones.map((ind, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-green-50 p-2 rounded text-sm">
                        <span><Badge variant="outline" className="mr-2">{ind.tipo}</Badge>{ind.descripcion}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(indicaciones, setIndicaciones, idx)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Controles */}
              <AccordionItem value="controles">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-orange-600" />
                    Controles ({controles.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  <div className="grid grid-cols-4 gap-2">
                    <Select value={newCtrl.tipo} onValueChange={(v) => setNewCtrl({ ...newCtrl, tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONTROL_TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Descripción" value={newCtrl.descripcion} onChange={(e) => setNewCtrl({ ...newCtrl, descripcion: e.target.value })} />
                    <Input placeholder="Frecuencia" value={newCtrl.frecuencia} onChange={(e) => setNewCtrl({ ...newCtrl, frecuencia: e.target.value })} />
                    <Button type="button" onClick={addControl} size="icon"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-2">
                    {controles.map((ctrl, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-orange-50 p-2 rounded text-sm">
                        <span><Badge variant="outline" className="mr-2">{ctrl.tipo}</Badge>{ctrl.descripcion} {ctrl.frecuencia && `- ${ctrl.frecuencia}`}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(controles, setControles, idx)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Derivaciones */}
              <AccordionItem value="derivaciones">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-600" />
                    Derivaciones ({derivaciones.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  <div className="grid grid-cols-4 gap-2">
                    <Input placeholder="Especialidad" value={newDer.especialidad} onChange={(e) => setNewDer({ ...newDer, especialidad: e.target.value })} />
                    <Input placeholder="Motivo" value={newDer.motivo} onChange={(e) => setNewDer({ ...newDer, motivo: e.target.value })} />
                    <Select value={newDer.urgencia} onValueChange={(v) => setNewDer({ ...newDer, urgencia: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {URGENCIA_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" onClick={addDerivacion} size="icon"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-2">
                    {derivaciones.map((der, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-purple-50 p-2 rounded text-sm">
                        <span><strong>{der.especialidad}</strong> - {der.motivo} <Badge variant="outline" className="ml-2">{der.urgencia}</Badge></span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(derivaciones, setDerivaciones, idx)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Notas */}
            <div>
              <Label>Notas adicionales</Label>
              <Textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Notas o consideraciones especiales..."
                rows={2}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting}
            data-testid="submit-treatment-plan"
          >
            {submitting ? 'Guardando...' : existingPlan ? 'Actualizar Plan' : 'Crear Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
